import { Component, HostListener, OnInit } from '@angular/core';
import { BaseComponent, DialogComponent, DialogService, WaitButtonComponent } from '@sircon/sircon-ui-framework';
import { Router } from '@angular/router';
import { AuthenticationService } from 'src/services/authentication.service';
import { UserContextService } from 'src/services/user-context.service';
import { takeUntil, tap } from 'rxjs';
import { EnvService } from 'src/services/env.service';
import { isEmpty } from 'lodash';
import { ProducerManagerIntegrationService } from 'src/services/producer-manager-integration.service';
import { MultiIndustryService } from 'src/services/multi-industry.service';

declare const app;

@Component({
    selector: 'app-unified-header',
    templateUrl: './unified-header.component.html',
    imports: [DialogComponent, WaitButtonComponent]
})
export class UnifiedHeaderComponent extends BaseComponent implements OnInit {

    submitting = false;
    accountSettings;
    producerCentralEnabled;
    isCarrierAccount;
    networkId;

    constructor(private router: Router,
                private env: EnvService,
                private authenticationService: AuthenticationService,
                private dialogService: DialogService,
                private userContextService: UserContextService,
                private producerManagerIntegrationService: ProducerManagerIntegrationService,
                private window: Window,
                private multiIndustryService: MultiIndustryService) {
        super();
    }

    @HostListener('shui-logo')
    onLogoEvent() {
        //eslint-disable-next-line
        !this.multiIndustryService.isInsuranceIndustry() ?
            this.router.navigate(['/dashboard/allActions']) : this.router.navigate(['/dashboard/activity']);
    }

    @HostListener('shui-logout')
    onLogoutEvent() {
        this.openLogoutOrExitDialog();
    }

    @HostListener('shui-industry-select', ['$event'])
    onIndustrySelectionEvent($event: CustomEvent) {
        this.userContextService.updateUserContext({ selectedIndustry: $event.detail.selectedIndustryType });
        this.router.navigate(['/dashboard/activity']);
    }

    @HostListener('shui-insurance-alert-select')
    OnAlertInsuranceIndustrySelection() {
        this.userContextService.updateUserContext({ selectedIndustry: 'INS' });
    }

    @HostListener('shui-navigate', ['$event'])
    onShuiNavigate($event) {
        if (app.util.browserCheck.browser.name === 'Internet Explorer') {
            this.setWindowLocation($event.detail.href);

            if ($event.detail.href.includes(this.getWindowHost())) {
                this.reloadWindow();
            }
        }
    }

    @HostListener('shui-account-settings')
    onAccountSettingsEvent() {
        if (this.accountSettings) {
            this.router.navigate(['/accountSettings']);
        } else {
            this.router.navigate(['/accountProfile']);
        }
    }

    getWindowHost() {
        return window.location.host;
    }

    private headerScriptInjected = false;

    ngOnInit() {
        this.userContextService.context$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((context) => {
                // Inject the external header script only once a real user context exists. The script's
                // one-shot init (sircon-header-ui) resolves the user from the PLM-USER-AUTH session cookie
                // and reveals its action menu groups. Because this component now persists across navigation
                // (CSS display instead of *ngIf - see app.component.html), that init only ever runs once.
                // Injecting it eagerly during the /authenticate redirect would run it before the session
                // exists, so it would fail and leave the menu groups hidden. Deferring until context loads
                // guarantees the cookie is set, matching the lazy header init UI-Router gave us for free.
                if (!isEmpty(context)) {
                    this.buildHeader();
                }
                this.updateUserContext(context);
            });
    }

    buildHeader() {
        // Guard against multiple context emissions re-injecting the script.
        if (this.headerScriptInjected) {
            return;
        }
        this.headerScriptInjected = true;

        const head = document.querySelector('head');
        const e = document.createElement('script');
        e.setAttribute('type', 'text/javascript');
        e.setAttribute('src', `${this.env.COMMON_HEADER_URL}/environment.js`);
        head.appendChild(e);
        const s = document.createElement('script');
        s.setAttribute('type', 'text/javascript');
        s.setAttribute('src', `${this.env.COMMON_HEADER_URL}/main.js`);
        head.appendChild(s);
    }

    openLogoutOrExitDialog() {
        if (this.interactiveReportingOnly()) {
            this.openExitDialog();
        } else {
            this.openLogoutDialog();
        }
    }

    interactiveReportingOnly() {
        return this.isCarrierAccount && !this.producerCentralEnabled;
    }

    updateUserContext(context) {
        // Only process and dispatch if context is not empty
        if (!isEmpty(context)) {
            this.accountSettings = context.permissions.routes.accountSettings;
            this.isCarrierAccount = this.userContextService.isCarrierAccount();
            if (this.isCarrierAccount) {
                this.networkId = this.userContextService.userContext.account.networkId;
                this.producerManagerIntegrationService.getBusinessRuleByNetworkId('PRODUCER_CENTRAL_ENABLED', this.networkId)
                    .pipe(
                        tap((response) => {
                            if (response && response['business-rules'] && response['business-rules'][0]) {
                                this.producerCentralEnabled = response['business-rules'][0].value === 'Y';
                            }
                        }),
                        takeUntil(this.ngUnsubscribe)
                    )
                    .subscribe();
            }

            // Dispatch event to framework header component after a small delay
            // This ensures framework components (like TopNavComponent) are fully initialized
            // The framework's HeaderComponent tries to set innerHTML on elements that may not exist
            // if the event is dispatched before the component is ready
            setTimeout(() => {
                const userContextEvent = document.createEvent('CustomEvent');
                userContextEvent.initCustomEvent('sui-user-context', true, true, { userContext: context });
                document.dispatchEvent(userContextEvent);
            }, 100);
        }
        // Don't dispatch event if context is empty/undefined - prevents framework header from crashing
    }

    openLogoutDialog() {
        this.dialogService.open('logout-dialog');
    }

    closeLogoutDialog() {
        this.dialogService.close('logout-dialog');
    }

    openExitDialog() {
        this.dialogService.open('exit-dialog');
    }

    closeExitDialog() {
        this.dialogService.close('exit-dialog');
    }

    logout() {
        this.submitting = true;
        // clear key after logout
        localStorage.removeItem('isMessageVisible');
        this.authenticationService.logout();
        this.closeLogoutDialog();
        // This header now persists across navigation (CSS display instead of *ngIf - see app.component.html),
        // so the instance is reused after logging back in. Reset submitting so a subsequent logout is not
        // stuck showing "Processing...". Previously UI-Router's reload:true recreated the component for us.
        this.submitting = false;
    }

    /**
     * Reloads the window.  This is split into a separate function for the purposes of unit tests as Jasmine v2.7.0
     * and greater do not allow spying on browser objects (such as window).  A wrapper function is created in order
     * for Jasmine to have something to spy on and prevent the browser reload.
     */
    reloadWindow() {
        this.window.location.reload();
    }

    setWindowLocation(href) {
        window.location.href = href;
    }

}
